import { CATEGORY, categoryDisplayNames } from '@brighte/enterprise-common/constants';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Heading } from '@spark-web/heading';
import { Inline } from '@spark-web/inline';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form, useActionData, useNavigation, useSubmit } from 'react-router';

import { Button as BrighteButton } from '~/components/common/button';
import { usePartnerConfig } from '~/context/PartnerConfigContext';
import { usePartner } from '~/hooks/usePartner';
import { useLeadContext } from '~/provider/LeadProvider';
import { useVendorContext } from '~/provider/VendorProvider';
import { action } from '~/routes/api+/marketplace+';
import { LDFeatureFlag } from '~/types/feature-flag';
import { CustomerFormValues, MarketplaceChannel } from '~/types/marketplace';

import { Alert } from '../common/alert';
import { BrighteLogo } from '../common/brighte-logo';
import { CategoryIcon } from '../common/category-icon';
import { ArrowRightIcon } from '../common/icon';
import { CustomerFormField } from '../form/components/customer-form-field';

import { VendorSelection } from './vendor-selection';

export type SelectionSummaryProps = {
  postcode: string;
  categories: string[];
  channel: MarketplaceChannel | null;
  categoryNames?: Record<string, string>;
};

export const SelectionSummary: FC<SelectionSummaryProps> = ({
  postcode,
  categories: urlCategories,
  channel,
  categoryNames,
}) => {
  // Prefer the API-provided name, then the local display-name map, then the raw slug.
  const getCategoryName = (category: string) =>
    categoryNames?.[category] ?? categoryDisplayNames[category as CATEGORY] ?? category;
  const theme = useTheme();
  const navigation = useNavigation();
  const actionData = useActionData<typeof action>();

  const { lead } = useLeadContext();
  const config = usePartnerConfig();
  const {
    requestQuoteModal,
    setRequestQuoteModal,
    selectedVendors,
    previouslySelectedVendorIds,
    reviewQuoteRef,
    isAutoSelecting,
  } = useVendorContext();

  const partner = usePartner();
  const activeChannel = config?.configuration?.channels?.find((c) => c.slug === channel);
  const channelFinancialProductId = activeChannel?.financialProductId;
  const { bypassContactFormToEnterMarketplace } = useFlags<LDFeatureFlag>();

  const formMethods = useForm<CustomerFormValues>({
    mode: 'all',
  });

  const {
    formState: { isValid },
    register,
    handleSubmit,
    setValue,
  } = formMethods;

  const submit = useSubmit();

  const selectedCategories = lead?.categorySlugs ?? [];
  const customerName = lead?.contact?.firstName;
  const title = customerName ? `${customerName}, let's request your quotes` : "Let's request your quotes";

  const isLoading = ['loading', 'submitting'].includes(navigation.state) || isAutoSelecting;
  const isCustomerFormFieldRequired = !lead && postcode !== '' && urlCategories.length > 0;
  const isSubmitDisabled = selectedVendors.length === 0 || (isCustomerFormFieldRequired ? !isValid : false);

  const selectedVendorsCategories = useMemo(
    () => new Set(selectedVendors.flatMap((vendor) => vendor.categories).map((category) => category?.slug)),
    [selectedVendors],
  );

  // set value to vendors form input when selectedVendors changes
  useEffect(() => {
    setValue(
      'vendors',
      JSON.stringify([
        ...previouslySelectedVendorIds.map((vendorId) => ({ vendorId })),
        ...selectedVendors.map((vendor) => ({
          vendorId: vendor.publicId,
          sortPosition: vendor.sortPosition,
          sortMethod: vendor.sortMethod,
        })),
      ]),
    );
  }, [selectedVendors, previouslySelectedVendorIds, setValue]);

  // set value to promotionCode form input when promotion filters changes
  const [promotions] = useQueryState('promotions', parseAsArrayOf(parseAsString).withDefault([]));
  useEffect(() => {
    setValue('promotionCode', (promotions && promotions?.length > 0 ? promotions[0] : lead?.promotionCode) || '');
  }, [promotions, lead?.promotionCode, setValue]);

  useEffect(() => {
    if (window && actionData?.redirectUrl) {
      window.location.href = actionData.redirectUrl;
    }
  }, [actionData]);

  const categoriesNotSelected = selectedCategories
    .filter((category) => !selectedVendorsCategories.has(category))
    .map((category) => getCategoryName(category));

  return (
    <ContentDialog
      title={<BrighteLogo />}
      showCloseButton
      isOpen={requestQuoteModal}
      onToggle={() => setRequestQuoteModal(false)}
      onCloseAutoFocus={(event: Event) => {
        event.preventDefault();
        reviewQuoteRef?.current?.focus();
      }}
      padding="none"
    >
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit((data) => submit(data, { method: 'post' }))}>
          <Stack gap="xxlarge">
            {isAutoSelecting ? (
              <Stack gap="large" align="center" paddingY="xxlarge">
                <Text size="small" align="center">
                  Finding the top vendors for you...
                </Text>
              </Stack>
            ) : (
              <Stack
                gap="xlarge"
                css={css({
                  opacity: isLoading ? 0.5 : 1,
                })}
              >
                <Stack gap="xxlarge">
                  <Heading level="2">{title}</Heading>
                  <Text size="small">
                    Your selected vendors will contact you to discuss your request and book a site visit. You will
                    receive emails from Brighte when quotes are ready for you to review and select.
                  </Text>
                </Stack>

                {isCustomerFormFieldRequired ? <CustomerFormField leadType="Marketplace" /> : null}

                {selectedCategories.length > 0 && (
                  <Stack gap="large" paddingY="medium">
                    <Text size="small" weight="semibold">
                      Upgrades selected
                    </Text>
                    {categoriesNotSelected.length > 0 ? (
                      <Box
                        paddingX="small"
                        paddingY="large"
                        css={css({ backgroundColor: theme.color.background.cautionLow })}
                      >
                        <Inline gap="xsmall">
                          <Text size="small" weight="semibold">
                            Please note:
                          </Text>
                          <Text size="small" color="textSecondary">
                            {`You haven't selected a vendor for your ${categoriesNotSelected.join(', ')}`}
                          </Text>
                        </Inline>
                      </Box>
                    ) : null}
                    {selectedCategories.map((category) => (
                      <Box key={category} display="flex" alignItems="center" flexDirection="row" gap="small">
                        <CategoryIcon slug={category} name={getCategoryName(category)} />
                        <Text size="small">{getCategoryName(category)}</Text>
                      </Box>
                    ))}
                  </Stack>
                )}

                <VendorSelection isMobile={false} />

                <input type="hidden" value={partner} {...register('partner')} />
                {channel && <input type="hidden" value={channel} {...register('channel')} />}
                {channelFinancialProductId && (
                  <input type="hidden" value={channelFinancialProductId} {...register('financialProductId')} />
                )}
                <input type="hidden" {...register('vendors')} />
                <input type="hidden" value={lead?.id} {...register('leadId')} />
                <input type="hidden" {...register('promotionCode')} />

                {!!actionData?.error && !isLoading && <Alert error="REQUEST_ERROR" partner={partner} />}
              </Stack>
            )}
          </Stack>

          <Stack
            background="surface"
            as="footer"
            position="sticky"
            gap="large"
            padding="large"
            css={{
              margin: -16,
              bottom: -24,
            }}
          >
            <BrighteButton
              rounded
              type="submit"
              loading={isLoading}
              disabled={isSubmitDisabled}
              label=""
              tone="primary"
            >
              <Inline gap="xsmall">
                Send quote requests
                <ArrowRightIcon partner={partner} />
              </Inline>
            </BrighteButton>
            {bypassContactFormToEnterMarketplace && (
              <BrighteButton
                rounded
                type="button"
                tone="neutral"
                onClick={() => {
                  datadogRum.addAction('Selected Modal Add Another Clicked');
                  setRequestQuoteModal(false);
                }}
              >
                Add another vendor
              </BrighteButton>
            )}
            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" gap="medium">
              <Text size="xsmall">Provided by</Text>
              <BrighteLogo />
            </Box>
          </Stack>
        </Form>
      </FormProvider>
    </ContentDialog>
  );
};
